import {ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {forkJoin, of, Subscription} from "rxjs";
import {catchError, map} from "rxjs/operators";
import {CoreComponent} from "../../../../core/component/core-component/core.component";
import {TicketsApiService} from "../../api/tickets-api.service";
import {ActivatedRoute} from "@angular/router";
import {TicketCommentDto, TicketDocumentDto, TicketDocumentListEntryDto, TicketDto} from "../../types/tickets.types";
import {UploadDocumentType} from "../../../../core/component/core-upload/core-upload.component";
import {MessageHelper} from "../../../../core/helper/message-helper";
import {ConfirmationService, MessageService} from "primeng/api";
import {AppMessageService} from "../../../../core/service/app-message.service";

import {DomSanitizer, SafeHtml} from '@angular/platform-browser';

interface ProcessedTicketComment extends TicketCommentDto {
  safeHtml: SafeHtml;
  isLoadingImages?: boolean;
}

interface DocumentFetchRequest {
  commentIndex: number;
  docId: number;
  fullImgTag: string;
}

interface DocumentFetchResult extends DocumentFetchRequest {
  blobUrl: string | null;
  error: boolean;
}

@Component({
  selector: 'app-ticket',
  templateUrl: './ticket.component.html',
  styleUrls: ['ticket.component.scss']
})
export class TicketComponent extends CoreComponent implements OnInit {

  ticketId?: number;
  ticket?: TicketDto;
  selectedDocument: TicketDocumentListEntryDto | undefined;
  visible: boolean = false;

  enteredComment?: string;

  selectedComment?: TicketCommentDto;
  isFileSelected = false;

  documentUploadBusy: boolean = false;

  processedComments: ProcessedTicketComment[] = [];
  private imageCache = new Map<number, string>(); // cache: docId -> blobUrl
  private refreshVersion = 0; // guards against race conditions during refresh


  constructor(
    protected confirmationService: ConfirmationService,
    protected ticketsApiService: TicketsApiService,
    protected messageService: AppMessageService,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef) {
    super();
  }

  override ngOnInit() {
    super.ngOnInit();


    this.route.paramMap.subscribe(params => {

      let ticketId = params.get('id');

      if (ticketId) {
        this.ticketId = +ticketId;
        this.refreshComponent();
      }

    });

  }

  handlePostMessage() {

    if (this.busy) {
      return;
    }

    if (this.ticketId && this.enteredComment && this.enteredComment != "") {

      this.busy = true;

      this.ticketsApiService.postComment(this.ticketId, {
        comment: this.enteredComment
      }).subscribe(value => {
        this.enteredComment = "";
        this.refreshComponent();
        this.busy = false;
      }, error => {
        MessageHelper.showErrorMessage(error.error.data, this.messageService)
        this.busy = false;
      });
    }

  }

  handleUpdateComment() {

    if (this.ticketId && this.enteredComment && this.enteredComment != "" && this.selectedComment) {

      this.busy = true;

      this.ticketsApiService.updateComment(this.ticketId, this.selectedComment.id, {
        comment: this.enteredComment
      }).subscribe(value => {
        this.enteredComment = "";
        this.selectedComment = undefined;
        this.refreshComponent();
        this.busy = false;
      }, error => {
        MessageHelper.showErrorMessage(error.error.data, this.messageService);
        this.busy = false;
      });
    }
  }

  handleDeleteComment() {

    if (this.ticketId && this.enteredComment && this.enteredComment != "" && this.selectedComment) {

      this.busy = true;

      this.ticketsApiService.deleteComment(this.ticketId, this.selectedComment.id).subscribe(value => {
        this.enteredComment = "";
        this.selectedComment = undefined;
        this.refreshComponent();
        this.busy = false;
      }, error => {
        MessageHelper.showErrorMessage(error.error.data, this.messageService);
        this.busy = false;
      });
    }

  }

  handleCancelUpdate() {
    this.selectedComment = undefined;
  }


  refreshComponent() {

    if (this.ticketId) {

      this.ticketsApiService.get(this.ticketId).subscribe(value => {
        this.ticket = value;
        this.handleTicketProcess();
        this.initialized = true;
      }, error => {
        MessageHelper.showErrorMessage(error.error.data, this.messageService);
      });
    }

  }

  handleFileSelect(event: File) {
    this.isFileSelected = true;
  }

  handleFileClear() {
    this.isFileSelected = false;
  }


  handleFileUpload(files: UploadDocumentType[]) {
    const uploadObservables = [];

    if (this.documentUploadBusy) {
      return;
    }

    this.documentUploadBusy = true;

    for (let file of files) {
      const dto: TicketDocumentDto = {
        name: file.name,
        dataBase64: file.fileBase64,
        mimeType: file.fileBase64MimeType
      };

      uploadObservables.push(this.ticketsApiService.createDocumentBase64(this.ticketId as number, dto));
    }

    forkJoin(uploadObservables).subscribe(results => {
      this.refreshComponent();

      this.documentUploadBusy = false;
    }, error => {
      this.documentUploadBusy = false;
      MessageHelper.showErrorMessage(error.error.data, this.messageService);
    });
  }


  handleDeleteDocument(event: MouseEvent, document: TicketDocumentListEntryDto) {

    if (this.documentUploadBusy) {
      return;
    }

    this.documentUploadBusy = true;

    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Bist du dir sicher?',
      header: 'Bestätigung',
      icon: 'pi pi-exclamation-triangle text-red-600',
      acceptIcon: "none",
      rejectIcon: "none",
      rejectButtonStyleClass: "p-button-text",
      accept: () => {
        this.ticketsApiService.deleteDocument(this.ticketId as number, document.id).subscribe(res => {
          this.messageService.showInfoMessage('', 'Dokument wurde gelöscht.');
          this.refreshComponent();
          this.documentUploadBusy = false;
        }, error => {
          MessageHelper.showErrorMessage(error.error.data, this.messageService);
          this.documentUploadBusy = false;
        })
      },
      reject: () => {
        this.messageService.showErrorMessage('Abgelehnt', 'Ihre Anfrage wurde abgelehnt.');
        this.documentUploadBusy = false;
      }
    });

  }

  handleTicketProcess(): void {

    // TICKET: DEF1000001263

    if (!this.ticket?.comments) return;

    // Sort comments by enteredOn (ascending - oldest first)
    const sortedComments = [...this.ticket.comments].sort((a, b) =>
      new Date(a.enteredOn).getTime() - new Date(b.enteredOn).getTime()
    );

    // Increment version to invalidate any in-flight async operations
    const currentVersion = ++this.refreshVersion;

    const imgTagRegex = /<img\b[^>]*\bsrc="documentId:(\d+)"[^>]*\s*\/?>/gi;

    // Step 1: Extract all document requests from all comments
    const allDocumentRequests: DocumentFetchRequest[] = [];
    sortedComments.forEach((comment, commentIndex) => {
      const matches = [...comment.comment.matchAll(imgTagRegex)];
      imgTagRegex.lastIndex = 0;
      for (const match of matches) {
        allDocumentRequests.push({
          commentIndex,
          docId: Number(match[1]),
          fullImgTag: match[0],
        });
      }
    });

    // Step 2: Separate cached vs uncached requests
    const cachedResults: DocumentFetchResult[] = [];
    const uncachedRequests: DocumentFetchRequest[] = [];

    for (const req of allDocumentRequests) {
      const cachedUrl = this.imageCache.get(req.docId);
      if (cachedUrl) {
        cachedResults.push({ ...req, blobUrl: cachedUrl, error: false });
      } else {
        uncachedRequests.push(req);
      }
    }

    // Step 3: Determine if we need to show loading indicators
    const hasUncachedImages = uncachedRequests.length > 0;

    // Step 4: Immediately render comments with cached images, mark uncached as loading
    this.processedComments = sortedComments.map((c, commentIndex) => {
      // Check if this comment has any uncached images
      const hasUncachedInThisComment = uncachedRequests.some(r => r.commentIndex === commentIndex);

      // Apply cached replacements immediately
      let html = c.comment;
      for (const cached of cachedResults.filter(r => r.commentIndex === commentIndex)) {
        const replacement =
          `<img src="${cached.blobUrl}"
              loading="lazy"
              alt="attachment"
              style="max-width:100%;height:auto;display:block;border-radius:6px;margin-top:4px;" />`;
        html = html.replace(cached.fullImgTag, replacement);
      }

      return {
        ...c,
        safeHtml: this.sanitizer.bypassSecurityTrustHtml(html),
        isLoadingImages: hasUncachedInThisComment,
      };
    });

    // If all images are cached, we're done
    if (!hasUncachedImages) {
      this.cdr.markForCheck();
      return;
    }

    // Step 5: Create observables only for uncached images
    const observables = uncachedRequests.map(req =>
      this.ticketsApiService.getDocumentBinary(this.ticketId as number, req.docId).pipe(
        map((blobUrl): DocumentFetchResult => ({
          ...req,
          blobUrl,
          error: false,
        })),
        catchError(() => of<DocumentFetchResult>({
          ...req,
          blobUrl: null,
          error: true,
        }))
      )
    );

    // Step 6: Fetch uncached documents in parallel
    forkJoin(observables).subscribe(results => {
      // Guard: abort if a newer refresh has started
      if (this.refreshVersion !== currentVersion) return;

      // Build a map of comment index -> current HTML (with cached images already applied)
      const commentHtmlMap = new Map<number, string>();
      sortedComments.forEach((c, i) => {
        // Get the original HTML and apply cached replacements
        let html = c.comment;
        for (const cached of cachedResults.filter(r => r.commentIndex === i)) {
          const replacement =
            `<img src="${cached.blobUrl}"
                loading="lazy"
                alt="attachment"
                style="max-width:100%;height:auto;display:block;border-radius:6px;margin-top:4px;" />`;
          html = html.replace(cached.fullImgTag, replacement);
        }
        commentHtmlMap.set(i, html);
      });

      // Apply newly fetched replacements and update cache
      for (const result of results) {
        let html = commentHtmlMap.get(result.commentIndex)!;

        if (result.error || !result.blobUrl) {
          const fallback =
            `<div class="img-placeholder"
                style="width:100%;height:120px;background:#f3f3f3;border-radius:6px;
                       display:flex;align-items:center;justify-content:center;margin-top:4px;">
             <span style="color:#999;font-size:12px;">Image unavailable</span>
           </div>`;
          html = html.replace(result.fullImgTag, fallback);
        } else {
          // Add to cache
          this.imageCache.set(result.docId, result.blobUrl);
          const replacement =
            `<img src="${result.blobUrl}"
                loading="lazy"
                alt="attachment"
                style="max-width:100%;height:auto;display:block;border-radius:6px;margin-top:4px;" />`;
          html = html.replace(result.fullImgTag, replacement);
        }

        commentHtmlMap.set(result.commentIndex, html);
      }

      // Update all processed comments with loaded images
      this.processedComments = sortedComments.map((c, i) => ({
        ...c,
        safeHtml: this.sanitizer.bypassSecurityTrustHtml(commentHtmlMap.get(i)!),
        isLoadingImages: false,
      }));

      this.cdr.markForCheck();
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    // Revoke all cached blob URLs to free memory
    this.imageCache.forEach(url => URL.revokeObjectURL(url));
    this.imageCache.clear();
  }


  onShowDocument(document: TicketDocumentListEntryDto) {
    this.selectedDocument = document;
    this.visible = true;

  }

}
